import type { ColDef } from "ag-grid-enterprise";
import { useMemo, useState } from "react";

import {
  useAutoAssignSearching,
  useOrder,
  useOrderMutation,
  useOrders,
  useReportsByOrderIds,
} from "@web-app/api";
import useDispatchStore from "@web-app/store/dispatchStore";
import { Order, Route } from "@web-app/types";

import {
  SortOptionType,
  filterOrdersBySearchTerm,
  sortOrders,
  sortRoutes,
} from "./utils";

export type SortParameter = {
  label: string;
  value: string;
};

export type GroupParameter = {
  label: string;
  value: string;
};

export type SidebarMode = "list" | "table";

export default function useOrderSidebar() {
  const [searchTerm, setSearchTerm] = useState("");
  const [routeToDelete, setRouteToDelete] = useState<string | null>(null);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);

  const { data, isFetching, isPending, isAssigneesLoading, isAppsLoading } =
    useOrders();
  const orders = data?.orders;
  const assigneesMap = data?.assignees || new Map();
  const appsMap = data?.apps || new Map();
  const { onCopy, onShare, onDelete } = useOrder();

  const orderIds = useMemo(() => {
    if (!orders) return [];
    return Array.from(orders.keys());
  }, [orders]);

  useReportsByOrderIds(orderIds, {
    enabled: orderIds.length > 0,
  });

  const {
    sidebarMode,
    focusedRouteId,
    focusedOrderId,
    groupParameter,
    isEditOrder,
    reportIssueOrderId,
    sortParameter,
  } = useDispatchStore();

  const setGroupParameter = (groupParameter: GroupParameter) =>
    useDispatchStore.setState({
      groupParameter,
    });

  const setSortParameter = (sortParameter: SortOptionType) =>
    useDispatchStore.setState({
      sortParameter,
    });

  const setSidebarMode = (mode: "list" | "table") =>
    useDispatchStore.setState({
      sidebarMode: mode,
    });

  const [unroutedOrders, routes, routedOrdersMap] = useMemo(() => {
    if (!orders) {
      return [];
    }

    let ordersArray = Array.from(orders.values());

    ordersArray = filterOrdersBySearchTerm(
      ordersArray,
      searchTerm,
      assigneesMap,
    );

    const routedOrders = ordersArray.filter((order) => order.routeId);
    const unroutedOrders = ordersArray.filter((order) => !order.routeId);

    const routesMap = new Map<string, Route>();
    const routedOrdersMap = new Map<string, Order>();

    for (const order of routedOrders) {
      const route = order.route;
      if (route) {
        routesMap.set(route.id, route);
      }

      routedOrdersMap.set(order.id, order);
    }

    const routes = Array.from(routesMap.values());

    const sortedRoutes = sortRoutes(routes, sortParameter, orders);
    const sortedUnroutedOrders = sortOrders(unroutedOrders, sortParameter);

    return [sortedUnroutedOrders, sortedRoutes, routedOrdersMap];
  }, [orders, searchTerm, sortParameter]);

  const focusedOrder = useMemo(() => {
    if (focusedOrderId) {
      const unroutedOrder = (unroutedOrders || []).find(
        (order) => order.id === focusedOrderId,
      );

      if (unroutedOrder) {
        return unroutedOrder;
      }

      const routedOrder = (routedOrdersMap || new Map()).get(focusedOrderId);

      if (routedOrder) {
        return routedOrder;
      }

      if (!isFetching) {
        useDispatchStore.setState({
          focusedOrderId: null,
        });
      }
    }

    return null;
  }, [focusedOrderId, unroutedOrders, routedOrdersMap, isFetching]);

  const unassignedEntityIds = useMemo(() => {
    const ids: string[] = [];
    for (const order of unroutedOrders || []) {
      if (!order.assigneeId) ids.push(order.id);
    }
    for (const route of routes || []) {
      if (!route.assigneeId) ids.push(route.id);
    }
    return ids;
  }, [unroutedOrders, routes]);

  const { searchingIds: autoAssignSearchingIds } =
    useAutoAssignSearching(unassignedEntityIds);

  const { deleteOrderById } = useOrderMutation();

  return {
    appsMap,
    assigneesMap,
    autoAssignSearchingIds,
    columnDefs,
    deleteOrderById,
    focusedOrder,
    focusedOrderId,
    focusedRouteId,
    groupParameter,
    isAppsLoading,
    isAssigneesLoading,
    isEditOrder,
    isLoading: isPending || isFetching,
    onCopy,
    onDelete,
    onShare,
    reportIssueOrderId,
    routedOrdersMap: routedOrdersMap || new Map(),
    routes: routes || [],
    routeToDelete,
    searchTerm,
    setColumnDefs,
    setGroupParameter,
    setRouteToDelete,
    setSearchTerm,
    setSidebarMode,
    setSortParameter,
    sidebarMode,
    sortParameter,
    unroutedOrders: unroutedOrders || [],
  };
}
